/**
 * useLayerSyncEffect — Effect 3 of the /map state machine.
 *
 * Spec: docs/superpowers/specs/2026-05-07-map-state-machine.md §6.2 + §7.4
 * Plan: docs/superpowers/plans/2026-05-07-sprint-2026-21-map-refactor.md Task 7
 *
 * Responsibility:
 *   Single source of truth for the dynamic filter on the
 *   'field-selected-highlight' layer added by the registry (T6) — the
 *   highlight overlay for the currently-selected field.
 *
 *   The filter is *derived* from MapState. The hook computes it with
 *   `useMemo` and pushes the result to maplibregl via `map.setFilter` in a
 *   minimal `useEffect`. Filter scattered across imperative event handlers
 *   was the source of the riskovaja-zone regressions (MapController twice
 *   broken / reverted) — declarative derivation eliminates that class of bug.
 *
 * История: до AK-428 (founder 2026-07-16) этот hook синхронизировал ещё и
 * фильтр 'fields-no-data-stripes' (серая заливка «нет данных» по coverage).
 * Слой удалён из registry — индикатор опирался на статистику пайплайна, а не
 * на реальные пиксели растра, и при дырах пайплайна «туманил» валидный NDVI.
 *
 * Selected-field filter — two shapes:
 *
 *   - selectedFieldId set    → `['==', ['get','id'], <id>]`
 *   - selectedFieldId null   → `['==', ['get','id'], '__none__']`
 *
 *   The __none__ sentinel matches the initial filter the layer is registered
 *   with (registry.ts T6). It guarantees no real id will match (UUID space
 *   never produces '__none__'), so the highlight layer renders nothing at
 *   boot and after CLEAR_SELECTED_FIELD.
 *
 * Defensive guards:
 *   - map === null  → no-op (initial render before maplibre boots).
 *   - getLayer(id) returns falsy → skip setFilter (defensive against being
 *     mounted before setupMapLayers has run).
 */
import { useEffect, useMemo } from 'react';
import type maplibregl from 'maplibre-gl';
import { useMapStore } from '../useMapStoreV2';
import { syncPillSelection } from '../pillSprite';

// MapLibre filter expressions are recursively typed; keeping the surface
// `unknown[]` here keeps the hook decoupled from maplibre's internal
// FilterSpecification union (which churns between minor versions). The
// narrow cast at the setFilter boundary is the only place we lie to TS.
type FilterExpr = unknown[];

const SENTINEL_NONE = '__none__';

// Layer id — must match registry.ts T6.
const LAYER_HIGHLIGHT = 'field-selected-highlight';

export function useLayerSyncEffect(map: maplibregl.Map | null): void {
  const selectedFieldId = useMapStore((s) => s.selectedFieldId);

  const selectedFieldFilter = useMemo<FilterExpr>(() => {
    return selectedFieldId
      ? ['==', ['get', 'id'], selectedFieldId]
      : ['==', ['get', 'id'], SENTINEL_NONE];
  }, [selectedFieldId]);

  useEffect(() => {
    if (!map) return;
    if (!map.getLayer(LAYER_HIGHLIGHT)) return;
    map.setFilter(LAYER_HIGHLIGHT, selectedFieldFilter as never);
  }, [map, selectedFieldFilter]);

  // AK-590: подпись выбранного поля — всегда ПОВЕРХ соседних (data-driven
  // symbol-sort-key) + подложка с жёлтым бордером (data-driven icon-image).
  // Отдельного symbol-слоя под выбранное поле НЕ заводим — это удержало бы
  // число per-field symbol-слоёв от снижения (freeze-бюджет, AK-134).
  useEffect(() => {
    if (!map) return;
    syncPillSelection(map, selectedFieldId);
  }, [map, selectedFieldId]);
}
